import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box, Tab, Button, CircularProgress,
} from '@mui/material';
import React, {
  useCallback, useEffect, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { DEGREE_DAY_NAME } from '../../../constants/graphs';
import ConversationsTab from '../ConversationTab';
import useStyles from '../styles';
import apiClient from '../../../requests/api/apiClient';
import { FetchError } from '../../../views/types';
import { DEFAULT_COLD_HOURS_CONFIG } from '../LineCharts/ColdHoursChart/types';
import DegreeDayCustomForm from './DegreeDayCustomForm';
import { ModalState, EMPTY_MODAL_STATE } from '../chartModal';
import useIsClientOperator from '../../../hooks/useIsClientOperator';

const DegreeDayConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { enqueueSnackbar } = useSnackbar();

  // Default to the conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    accumulatedDegreesBaseTemp: DEFAULT_COLD_HOURS_CONFIG.accumulatedDegreesBaseTemp,
    accumulatedColdHoursBaseTemp: DEFAULT_COLD_HOURS_CONFIG.coldHoursBaseTemp,
  });

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const handleError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    } else {
      const { error: apiError } = error as FetchError;
      enqueueSnackbar(apiError?.message || 'Ha ocurrido un error inesperado', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!sectorId) return;
      setIsLoading(true);
      try {
        const { data } = await apiClient.degreeDayConfig.get(sectorId);
        if (data?.degreeDayConfig) {
          setConfig({
            accumulatedDegreesBaseTemp:
              data.degreeDayConfig.accumulatedDegreesBaseTemp !== undefined
                && data.degreeDayConfig.accumulatedDegreesBaseTemp !== null
                ? data.degreeDayConfig.accumulatedDegreesBaseTemp
                : DEFAULT_COLD_HOURS_CONFIG.accumulatedDegreesBaseTemp,
            accumulatedColdHoursBaseTemp:
              data.degreeDayConfig.accumulatedColdHoursBaseTemp !== undefined
                && data.degreeDayConfig.accumulatedColdHoursBaseTemp !== null
                ? data.degreeDayConfig.accumulatedColdHoursBaseTemp
                : DEFAULT_COLD_HOURS_CONFIG.coldHoursBaseTemp,
          });
        }
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [sectorId, handleError]);

  const handleConfigChange = useCallback((field: string, value: string | number) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        accumulatedDegreesBaseTemp: Number(config.accumulatedDegreesBaseTemp),
        accumulatedColdHoursBaseTemp: Number(config.accumulatedColdHoursBaseTemp),
      };
      const { data: response } = await apiClient.degreeDayConfig.post(sectorId, payload);
      enqueueSnackbar(response.message, { variant: 'success' });
      // Saved base temperatures affect chart series data — trigger a refresh.
      onConfigSaved?.();
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  }, [sectorId, config, enqueueSnackbar, handleError, onConfigSaved]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Alertas" value="2" className={classes.tabLabel} />
          <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <DegreeDayCustomForm
                config={config}
                onConfigChange={handleConfigChange}
              />
              <Box mt={4} display="flex" justifyContent="flex-end">
                {saving ? <CircularProgress /> : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ minWidth: 140, fontSize: 18, py: 1.5 }}
                  >
                    GUARDAR
                  </Button>
                )}
              </Box>
            </>
          )}
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        In progress
      </TabPanel>
      <TabPanel value="3" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={DEGREE_DAY_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default DegreeDayConfigContent;
